import MetricFilterTypes from "@Enums/MetricFilterTypes";
import PlayModes from "@Enums/PlayModes";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export type AppBarItem = {
  index: number;
  id: string;
  icon: IconDefinition;
  path: string;
  disabled?: boolean;
};

export interface PageRoute {
  id: string;
  path: string;
}

export type TooltipConfig = {
  el: (props: any, previous?: any, graphProps?: any) => JSX.Element;
  children?: (props: any) => JSX.Element;
};

export type SessionScoreResult = {
  final_score: number;
  target_score: number;
};

export type ScanFrequencyResult = {
  final_score: number;
  raw_result: number;
  target_score: number;
  isPlaySession?: boolean;
  context: {
    baseScanFrequency: number;
    scanFrequency: number;
  };
};

export type ScanTimingResult = {
  final_score: number;
  raw_result: number;
  target_score: number;
  isPlaySession?: boolean;
  context: {
    correctly_timed_scans: number;
    failed_receival_penality: number;
    failed_receivals: number;
    incorrectly_timed_scans: number;
    unseen_kicks: number;
    unseen_receivals: number;
  };
};

export type DecisionResult = {
  final_score: number;
  raw_result: number;
  target_score: number;
  isPlaySession?: boolean;
  context: {
    failedShots: number;
    offsides: number;
    passes: number;
    successfulShots: number;
    tackles: number;
  };
};

export type MemoryResult = {
  final_score: number;
  raw_result: number;
  target_score: number;
  isPlaySession?: boolean;
  context: {
    correct_labels: number;
    incorrect_labels: number;
    test_count: number;
  };
};

export type CriticalScanResult = {
  final_score: number;
  raw_result: number;
  target_score: number;
  isPlaySession?: boolean;
  context: {
    critical_scan_reward: number;
    critical_scans: number;
    critically_scanned_receivals: number;
    failed_receival_penality: number;
    failed_receivals: number;
    total_receivals: number;
  };
};

export interface IPlayItemResult {
  id?: number;
  byb_iq: number;
  scan_rate: number;
  scan_timing: number;
  critical_scans: number;
  passes_score: number;
  awareness_score: number;
  gameplay_duration: number;
  cognitive_result_id: number;
  total_score: number;
  labels_made: number;
  labels_succeeded: number;
  play_mode_config: {
    mode: PlayModes;
  };
  scores?: {
    [key in MetricFilterTypes]?: {
      final_score: number;
    };
  };
}

export type Subscription = {
  id: string;
  type: string;
  description: string;
};

export type Division = {
  name: string;
  rating_point: number;
  rating_config?: {
    failure_penality?: number;
    success_reward?: number;
    bonus_multiplier?: number;
    failed_receival_penalty?: number;
    critical_scan_reward?: number;
    bonus_cap?: number;
    progression_failure_limit?: number;
    progression_attempt_limit?: number;
  };
  score_config?: any;
  play_item_config?: any;
  briefing?: string;
  promotion_message?: string;
  relegation_message?: string;
  video_id?: string;
};

export type League = {
  name: string;
  rating_point?: number;
  divisions?: Division[];
  promotion_message?: string;
  relegation_message?: string;
};

export type UserRankResult = {
  rank: number;
  doc_id: string;
  first: string;
  last: string;
  avatar: string | undefined;
  score: number;
  rating: number;
  leaderboard_anonymity: boolean;
};

export type IncomingReplayEventData = {
  cognitive_result_id: number;
  retry_attempt_id?: string;
  progress?: number;
  playback_state?: "playing" | "paused";
  data?: any;
};

export type IncomingReplayEvent = {
  action: string;
  cognitive_result_id?: number;
  retry_attempt_id?: string;
  data?: IncomingReplayEventData[] | IncomingReplayEventData;
};

export type IncomingScenarioRetryReplay = {
  retry_attempt_id: string;
  original_cognitive_result_id: number;
  status: "completed" | "cancelled" | "failed";
  replay_data?: unknown;
};

export type IncomingReplayUiEvent = {
  action: string;
  feature: string;
  value: boolean;
};

export type Option = {
  time?: number;
  ballPosition?: { x: number; y: number; z: number };
  option?: {
    curve01: number;
    heightBall: boolean;
    offensiveness: number;
    opportunity: number;
    passPosition: { x: number; y: number; z: number };
    receiverDistanceToPassPoint: number;
    receiverEntityIndex: number;
    receiverPosition: { x: number; y: number; z: number };
    safety: number;
    score: number;
  };
};

export type Decision = {
  options: Option[];
  userEndTime: number;
  userPassIndex: number;
  userStartTime: number;
};

export type IncomingReplayPassOptionsEvent = {
  decisions: Decision[];
};

export enum GameplayModule {
  PLAYCORE = "playcore",
  BYBCAT = "bybcat",
  REPLAY = "replay",
  MATCHPLAY = "matchplay",
}

export enum GameplayAction {
  INIT = "init",
  ENTER = "enter",
  EXIT = "exit",
}

export type GameplayEvent = {
  module: GameplayModule;
  action: GameplayAction;
};

export type BybCatResult = {
  group: string | null;
  scenarioCount: number;
  scanCount: number;
  scanRate: number;
  scanTimingPercentage?: number; // Placeholder for special metric result
  scanTiming: number;
  scanSymmetry: number;
  scanCriticalLegacyCount: number;
  scanCriticalCount: number;
  inhibition?: number; // Placeholder for special metric result
  inhibitionPercentage?: number; // Placeholder for special metric result
  inhibitionTestCount: number;
  inhibitionCorrect: number;
  inhibitionFalsePass: number;
  inhibitionIncorrect: number;
  inhibitionAverageDelay: number;
  inhibitionMinDelay: number | string | null;
  passCorrect: number;
  passIncorrect: number;
  WM?: number; // Placeholder for special metric result
  WMPercentage?: number; // Placeholder for special metric result
  WMTestCount: number;
  WMCorrect: number;
  WMIncorrect: number;
  WMUnanswered: number;
  WMTime: number;
  PR?: number; // Placeholder for special metric result
  PRPercentage?: number; // Placeholder for special metric result
  PRTestCount: number;
  PRCorrect: number;
  PRIncorrect: number;
  PRUnanswered: number;
  PRTime: number;
  passPackingRate: number;
  possessionTime: number;
};

export type BybCatTestSession = {
  name?: string;
  code: number;
  date: string;
  aggregateResults: BybCatResult;
  slicedResults: Array<BybCatResult>;
  individualResults: Array<BybCatResult>;
};

export type BybCatPlayerResult = {
  name: string;
  averageResults?: { [key: string]: number };
  aggregateResults?: BybCatResult;
  testSessions: Array<BybCatTestSession>;
};

export type IncomingBybCatEventData = {
  players: Array<BybCatPlayerResult>;
};

export type IncomingBybCatEvent = {
  action: "set_results" | "show_results";
  results?: IncomingBybCatEventData;
  player_index?: number;
  test_index?: number;
};

export type OutgoingBybCatEvent = {
  action: "hide_results";
};

export type SetHeaderAltSectionsEvent = {
  leftSection?: React.ReactNode;
  centerSection?: React.ReactNode;
  rightSection?: React.ReactNode;
} | null;

export type NavigationEvent = {
  path: string;
  data?: any;
  delay?: number;
  replace?: boolean; // Replace the last path in the history
  reset?: boolean; // Clear the history before adding the new path
  resetTo?: string; // Clear any history after this path before adding the new path
  onNavigated?: () => void;
};

export interface VRHeaderButtonAction {
  label: string;
  onClick: () => void;
  color?: "primary" | "secondary";
  disabled?: boolean;
  sx?: any;
}

export interface VRHeaderButtonProps {
  title: string;
  badge?: string;
  actions?: VRHeaderButtonAction[];
}

export type VRHeaderButtonEvent = {
  type: VRHeaderButtonType;
  title: string;
  badge?: string;
  actions?: VRHeaderButtonAction[];
};

